class DateUtils {

    /**
     * Convierte un string ISO 8601 al formato YYYY-MM-DD requerido por <input type="date">.
     * Construye la fecha usando los métodos del objeto Date en lugar de substring() para
     * manejar correctamente strings ISO con zona horaria (p.ej. "2024-01-15T23:00:00.000Z"
     * puede pertenecer al día 16 en UTC+1 si se trunca sin convertir).
     * Devuelve cadena vacía si el valor es nulo o no parseable.
     * @param {string|null|undefined} isoString - Cadena en formato ISO 8601
     * @returns {string} Fecha en formato YYYY-MM-DD para uso en inputs, o cadena vacía
     */
    static convertirIsoAInputFecha(isoString) {
        if (!isoString) return '';
        try {
            const date = new Date(isoString);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        } catch {
            return '';
        }
    }

    /**
     * Extrae la parte de fecha YYYY-MM-DD de un string ISO 8601 o date-only.
     * Los strings date-only (YYYY-MM-DD) ya tienen el formato correcto y se devuelven tal cual.
     * Los strings con hora (YYYY-MM-DDTHH:mm:ssZ) se truncan eliminando la parte horaria.
     * Útil para comparar rangos de fechas sin que la zona horaria del cliente distorsione el resultado.
     * @param {string|null|undefined} isoString - Cadena en formato ISO 8601 o date-only
     * @returns {string} Fecha en formato YYYY-MM-DD, o cadena vacía si el valor es nulo/vacío
     */
    static extraerSoloFecha(isoString) {
        if (!isoString) return '';
        return isoString.substring(0, 10);
    }

    /**
     * Formatear una fecha ISO 8601 al formato localizado es-ES (dd/mm/aaaa).
     * Devuelve cadena vacía si el valor es nulo/undefined, y el valor original
     * si la fecha no es parseable.
     * @param {string|null|undefined} isoString - Cadena de fecha en formato ISO 8601
     * @returns {string} Fecha formateada o cadena vacía en caso de valor vacío
     */
    static formatearFecha(isoString) {
        if (!isoString) return '';
        try {
            const date = new Date(isoString);
            return date.toLocaleDateString('es-ES', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            });
        } catch {
            return isoString;
        }
    }

    /**
     * Formatear una fecha ISO 8601 al formato localizado es-ES con hora (dd/mm/aaaa hh:mm).
     * Devuelve cadena vacía si el valor es nulo/undefined, y el valor original
     * si la fecha no es parseable.
     * @param {string|null|undefined} isoString - Cadena de fecha en formato ISO 8601
     * @returns {string} Fecha y hora formateadas o cadena vacía en caso de valor vacío
     */
    static formatearFechaHora(isoString) {
        if (!isoString) return '';
        try {
            const date = new Date(isoString);
            const fechaParte = date.toLocaleDateString('es-ES', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            });
            const horaParte = date.toLocaleTimeString('es-ES', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });
            return `${fechaParte} ${horaParte}`;
        } catch {
            return isoString;
        }
    }
}

export default DateUtils;